import React, {
  useState, useEffect, useRef, useLayoutEffect,
} from 'react';
import {
  CloseOutlined,
  FullscreenOutlined,
  SendOutlined,
  UploadOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {
  Spin, Input, Button, message, Upload,
} from 'antd';
import ReactMarkdown from 'react-markdown';
import PropTypes from 'prop-types';

import OttoIcon from './OttoIcon';

const { TextArea } = Input;
const termsUrl = 'https://ontraccr-public.s3.us-west-2.amazonaws.com/Ontraccr+Chatbot+%E2%80%93+Terms+%26+Conditions+of+Use.pdf';

function OttoChatBase({
  visible,
  onClose,
  title = 'Otto: Your AI Assistant',
  welcomeMessage,
  placeholder = 'Ask me anything',
  loadingText = 'Otto is thinking...',
  headerActions,
  onSend,
  initialMessage,
  defaultWidth = '480px',
  fullscreenWidth = 'calc(100vw - 220px)',
  showDisclaimer = true,
  onFileUpload,
  uploadedFile,
  onRemoveFile,
}) {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const chatContentRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleFileChange = (info) => {
    const { fileList } = info;
    const file = fileList[0]?.originFileObj || fileList[0];

    if (file) {
      const fileType = file.type?.toLowerCase();
      const fileName = file.name?.toLowerCase();
      const isPDF = fileType === 'application/pdf' || fileName.endsWith('.pdf');

      if (!isPDF) {
        message.error('Only PDF files are supported');
        return;
      }

      onFileUpload(file);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const beforeUpload = (file) => {
    const fileType = file.type?.toLowerCase();
    const fileName = file.name?.toLowerCase();
    const isPDF = fileType === 'application/pdf' || fileName.endsWith('.pdf');

    if (!isPDF) {
      message.error('Only PDF files are supported');
      return false;
    }

    if (uploadedFile) {
      message.info('Replacing existing file');
    }

    return true;
  };

  // Initialize with initial message if provided
  useEffect(() => {
    if (visible && messages.length === 0 && initialMessage) {
      setMessages([initialMessage]);
    }
  }, [visible, initialMessage]);

  // Find the latest user message and scroll so that it is at the top of the visible chat window
  const scrollToLatestMessage = () => {
    if (!chatContentRef.current) {
      return;
    }

    const userMessages = chatContentRef.current
      .querySelectorAll('.otto-chat-message-container:is(.user-message)');

    if (userMessages.length === 0) {
      return;
    }

    const latestUserMessage = userMessages[userMessages.length - 1];
    latestUserMessage.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  useLayoutEffect(() => {
    scrollToLatestMessage();
  }, [messages, loading]);

  const addMessage = (message) => {
    setMessages((prevMessages) => [...prevMessages, message]);
  };

  const handleSend = async () => {
    const trimmedInput = inputValue.trim();
    if (trimmedInput && onSend) {
      setLoading(true);
      const newMessages = [...messages,
        {
          role: 'user',
          content: trimmedInput,
          attachedFile: uploadedFile,
        },
      ];
      setMessages(newMessages);
      setInputValue('');

      if (uploadedFile && onRemoveFile) {
        onRemoveFile(uploadedFile.id);
      }

      try {
        const result = await onSend(trimmedInput, threadId, uploadedFile);

        if (result?.threadId) {
          setThreadId(result.threadId);
        }

        if (result?.response) {
          newMessages.push({
            role: 'assistant',
            content: result.response,
          });
          setMessages(newMessages);
        }
      } catch (error) {
        console.error('Error sending message:', error);
        newMessages.push({
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again.',
        });
        setMessages(newMessages);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!visible) {
      setIsFullscreen(false);
    }
  }, [visible]);

  let height = 0;
  let width = 0;
  if (visible) {
    height = isFullscreen ? '100vh' : '565px';
    width = isFullscreen ? fullscreenWidth : defaultWidth;
  }

  const containerStyle = {
    width,
    height,
    borderRadius: isFullscreen && visible ? 0 : '12px',
    border: isFullscreen || !visible ? 'none' : '1px solid #FFEC89',
    boxShadow: isFullscreen && visible ? 'none' : '0px 0px 30px 0px #ED4C0933',
    transform: isFullscreen && visible ? 'translateX(calc(20px)) translateY(-80px)' : 'translateX(0)',
  };

  // Clone headerActions and inject addMessage if it's a function component or render prop
  const renderHeaderActions = () => {
    if (typeof headerActions === 'function') {
      return headerActions({ addMessage });
    }
    return headerActions;
  };

  return (
    <div className="otto-chat-container" style={containerStyle}>
      {/* Header */}
      <div className="otto-chat-header">
        <div className="otto-chat-header-left">
          <OttoIcon isHeader />
          <span className="otto-chat-title">{title}</span>
          <span className="otto-chat-beta-tag">Beta</span>
        </div>
        <div className="otto-chat-header-right">
          {renderHeaderActions()}
          <FullscreenOutlined
            className="otto-chat-action-icon"
            onClick={() => setIsFullscreen(!isFullscreen)}
          />
          <CloseOutlined className="otto-chat-action-icon" onClick={onClose} />
        </div>
      </div>

      <div
        ref={chatContentRef}
        className={`otto-chat-content ${isFullscreen ? 'otto-chat-fullscreen' : ''}`}
      >
        {
          messages.length === 0 && !loading && (
            <div className="otto-chat-welcome-text">
              {welcomeMessage || 'Welcome! Feel free to ask me anything. How can I help?'}
            </div>
          )
        }

        {
          messages.length > 0 && (
            messages.map((message, index) => (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={`${message.role}-${index}`}
                className={`otto-chat-message-container ${message.role === 'user' ? 'user-message' : ''}`}
              >
                {message.role === 'assistant' && (
                  <div style={{ marginTop: '4px' }}>
                    <OttoIcon isHovered={false} showStars={false} />
                  </div>
                )}
                <div
                  className={`otto-chat-message-bubble ${message.role === 'user' ? 'user-message' : ''}`}
                >
                  {
                    message.role === 'assistant' ? (
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    ) : (
                      <div>
                        <div>{message.content}</div>
                        {message.attachedFile && (
                          <div className="otto-chat-attached-file">
                            <FileTextOutlined className="otto-chat-attached-file-icon" />
                            <span className="otto-chat-attached-file-name">
                              {message.attachedFile.name}
                            </span>
                          </div>
                        )}
                      </div>
                    )
                  }
                </div>
              </div>
            ))
          )
        }

        {loading && (
          <div className="otto-chat-message-container">
            <div style={{ marginTop: '4px' }}>
              <OttoIcon isHovered={false} showStars={false} />
            </div>
            <div className="otto-chat-message-bubble">
              <Spin size="small" style={{ marginRight: '8px' }} />
              <span>{loadingText}</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className={`otto-chat-footer-container ${isFullscreen ? 'otto-chat-fullscreen' : ''}`}>
        {uploadedFile && (
          <div className="otto-chat-uploaded-file">
            <div className="otto-chat-uploaded-file-content">
              <FileTextOutlined className="otto-chat-uploaded-file-icon" />
              <span className="otto-chat-uploaded-file-name">
                {uploadedFile.name}
              </span>
            </div>
            {onRemoveFile && (
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => onRemoveFile(uploadedFile.id)}
                style={{ padding: '0 4px', minWidth: 'auto' }}
                title="Remove file"
              />
            )}
          </div>
        )}

        <div className="otto-chat-input-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {onFileUpload && (
            <Upload
              accept=".pdf,application/pdf"
              beforeUpload={beforeUpload}
              showUploadList={false}
              customRequest={({ onSuccess }) => {
                setTimeout(() => onSuccess('ok'), 0);
              }}
              onChange={handleFileChange}
              fileList={[]}
            >
              <Button
                type="text"
                icon={<UploadOutlined />}
                disabled={loading}
                style={{
                  padding: '4px 8px',
                  flexShrink: 0,
                }}
                title="Upload PDF example file"
              />
            </Upload>
          )}
          <TextArea
            value={inputValue}
            onChange={(e) => {
              if (!loading) setInputValue(e.target.value);
            }}
            onPressEnter={(e) => {
              if (!loading && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={placeholder}
            className="otto-chat-input"
            autoSize={{ minRows: 1, maxRows: 10 }}
          />
          <Button
            type="text"
            icon={<SendOutlined />}
            onClick={handleSend}
            className="otto-chat-send-button"
            disabled={loading}
          />
        </div>
        {showDisclaimer && (
          <p className="otto-chat-disclaimer">
            By using this chatbot (currently called "Otto"), you acknowledge and agree to the{' '}
            <a href={termsUrl} target="_blank" rel="noopener noreferrer">
              Chatbot Terms & Conditions
            </a>
            .
          </p>
        )}
      </div>
    </div>
  );
}

OttoChatBase.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string,
  welcomeMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  placeholder: PropTypes.string,
  loadingText: PropTypes.string,
  headerActions: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  onSend: PropTypes.func.isRequired,
  initialMessage: PropTypes.shape({
    role: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
  }),
  defaultWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fullscreenWidth: PropTypes.string,
  showDisclaimer: PropTypes.bool,
  onFileUpload: PropTypes.func,
  uploadedFile: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
  }),
  onRemoveFile: PropTypes.func,
};

export default OttoChatBase;
